import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ProductCard from './ProductCard';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const ProductSlider = ({ products, title = "Shop Products", compact = false, showCount = true }) => {
  const { t } = useTranslation('shop');
  const sliderRef = useRef(null);

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = compact ? 200 : 320;
      const newPosition = direction === 'left' 
        ? sliderRef.current.scrollLeft - scrollAmount
        : sliderRef.current.scrollLeft + scrollAmount;
      
      sliderRef.current.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
    }
  };

  if (!products || products.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">📦</span>
        </div>
        <p className="text-gray-500 text-lg font-semibold">No products available yet</p>
        <p className="text-gray-400 text-sm mt-2">Check back soon for new programs!</p>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Title */}
      {title && (
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">{title}</h2>
      )}

      <div className="relative">
        {/* Navigation Buttons - Desktop Only */}
        <div className="hidden md:block">
          <button
            onClick={() => scroll('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 shadow-xl rounded-full transition-all hover:scale-110 active:scale-95 ${
              compact ? 'p-2 -ml-4' : 'p-3 -ml-6'
            }`}
            aria-label={t('common:ariaLabels.scrollLeft')}
          >
            <ChevronLeftIcon className={compact ? 'h-5 w-5 text-gray-700' : 'h-6 w-6 text-gray-700'} />
          </button>
          <button
            onClick={() => scroll('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 shadow-xl rounded-full transition-all hover:scale-110 active:scale-95 ${
              compact ? 'p-2 -mr-4' : 'p-3 -mr-6'
            }`}
            aria-label={t('common:ariaLabels.scrollRight')}
          >
            <ChevronRightIcon className={compact ? 'h-5 w-5 text-gray-700' : 'h-6 w-6 text-gray-700'} />
          </button>
        </div>

        {/* Product Slider */}
        <div
          ref={sliderRef}
          className={`flex overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth ${
            compact ? 'gap-3 pb-2' : 'gap-4 md:gap-6 pb-4'
          }`}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {products.map((product) => (
            <div
              key={product.id}
              className={`flex-shrink-0 snap-start ${
                compact 
                  ? 'w-44 sm:w-48' 
                  : 'w-64 sm:w-72 md:w-80'
              }`}
              style={{ minWidth: compact ? '168px' : '256px' }}
            >
              <ProductCard product={product} compact={compact} />
            </div>
          ))}
          
          {/* Spacer for last item padding */}
          <div className="flex-shrink-0 w-1" />
        </div>

        {/* Scroll Indicator - Mobile */}
        <div className="flex justify-center gap-1.5 mt-4 md:hidden">
          {products.map((_, index) => (
            <div
              key={index}
              className="h-1.5 w-1.5 rounded-full bg-gray-300"
            />
          ))}
        </div>
      </div>

      {/* Products Count - Only show if showCount is true */}
      {showCount && (
        <div className="text-center mt-4 text-sm text-gray-500">
          {t('productSlider.programsAvailable', { count: products.length })}
        </div>
      )}
    </div>
  );
};

export default ProductSlider;
